// EditorCanvas - S47 / S48.1.
// Canvas WYSIWYG. S48.1: acepta prop `bundle` ademas de `producto` y la
// propaga a FieldRenderer para resolver bindKeys de tipo bundle.*.
import { useEffect, useMemo, useRef } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { DND_MIME, PX_PER_MM } from '../constants';
import { findSize, pxToMm } from '../utils';
import FieldRenderer from './FieldRenderer';

const CSS_ID = 'xl-et-canvas-css';
const CSS = `
.xl-et-canvas-wrap {
  display: flex; align-items: flex-start; justify-content: center;
  padding: 16px;
  background: var(--xl-color-surface-alt);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  min-height: 320px;
  font-family: var(--xl-font-sans);
}
.xl-et-canvas {
  position: relative;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  box-shadow: var(--xl-shadow-md);
  background-image:
    linear-gradient(to right, var(--xl-color-border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--xl-color-border) 1px, transparent 1px);
  background-size: var(--grid-step) var(--grid-step);
  background-position: 0 0;
  overflow: hidden;
  cursor: default;
}
.xl-et-canvas--over { outline: 2px dashed var(--xl-color-focus); outline-offset: -2px }

.xl-et-field {
  position: absolute;
  border: 1px dashed var(--xl-color-text-muted);
  background: rgba(37, 99, 235, 0.04);
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
}
.xl-et-field:hover { border-color: var(--xl-color-text) }
.xl-et-field--selected {
  border: 1.5px solid var(--xl-color-focus);
  background: rgba(37, 99, 235, 0.10);
}

.xl-et-empty {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--xl-color-text-muted);
  font: 500 12px/1.4 var(--xl-font-sans);
  pointer-events: none;
  text-align: center;
  padding: 12px;
}
`;

function _parsePayload(dt) {
  let raw = null;
  try { raw = dt.getData(DND_MIME); } catch (_) { raw = null; }
  if (raw) {
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj.type === 'string') return obj;
    } catch (_) { /* fallthrough */ }
  }
  let plain = null;
  try { plain = dt.getData('text/plain'); } catch (_) { plain = null; }
  if (plain) return { type: plain, bindKey: null };
  return null;
}

export default function EditorCanvas({
  sizeId,
  fields,
  selectedId,
  onAddField,
  onSelectField,
  producto,
  bundle,
  zoom = 1.5,
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const ref = useRef(null);
  const sz = useMemo(() => findSize(sizeId), [sizeId]);

  const scale = PX_PER_MM * Number(zoom || 1);
  const wPx = sz.wMm * scale;
  const hPx = sz.hMm * scale;
  const stepPx = scale;

  const onDragOver = (e) => {
    if (e.dataTransfer && e.dataTransfer.types) {
      const types = Array.prototype.slice.call(e.dataTransfer.types);
      if (types.indexOf(DND_MIME) !== -1 || types.indexOf('text/plain') !== -1) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        const t = e.currentTarget;
        if (t && t.classList) t.classList.add('xl-et-canvas--over');
      }
    }
  };
  const onDragLeave = (e) => {
    const t = e.currentTarget;
    if (t && t.classList) t.classList.remove('xl-et-canvas--over');
  };
  const onDrop = (e) => {
    e.preventDefault();
    const t = e.currentTarget;
    if (t && t.classList) t.classList.remove('xl-et-canvas--over');
    const payload = _parsePayload(e.dataTransfer);
    if (!payload) return;
    const rect = t.getBoundingClientRect();
    const xPx = e.clientX - rect.left;
    const yPx = e.clientY - rect.top;
    const xMm = pxToMm(xPx / Number(zoom || 1));
    const yMm = pxToMm(yPx / Number(zoom || 1));
    if (typeof onAddField === 'function') {
      onAddField({
        type: payload.type,
        bindKey: payload.bindKey || null,
        wMm: payload.wMm,
        hMm: payload.hMm,
        xMm: xMm,
        yMm: yMm,
      });
    }
  };

  const onCanvasClick = (e) => {
    if (e.target === ref.current && typeof onSelectField === 'function') {
      onSelectField(null);
    }
  };

  return (
    <div className="xl-et-canvas-wrap">
      <div
        ref={ref}
        className="xl-et-canvas"
        style={{
          width: wPx + 'px',
          height: hPx + 'px',
          '--grid-step': stepPx + 'px',
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onCanvasClick}
        aria-label={'Lienzo de etiqueta ' + sz.label}
      >
        {fields.length === 0 && (
          <div className="xl-et-empty">
            Arrastra un campo desde el panel izquierdo.
          </div>
        )}
        {fields.map((f) => {
          const isSel = f.id === selectedId;
          const cls = 'xl-et-field' + (isSel ? ' xl-et-field--selected' : '');
          return (
            <div
              key={f.id}
              className={cls}
              style={{
                left:   (f.xMm * scale) + 'px',
                top:    (f.yMm * scale) + 'px',
                width:  (f.wMm * scale) + 'px',
                height: (f.hMm * scale) + 'px',
              }}
              onClick={(ev) => {
                ev.stopPropagation();
                if (typeof onSelectField === 'function') onSelectField(f.id);
              }}
            >
              <FieldRenderer field={f} producto={producto} bundle={bundle} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
